import { consola } from 'consola';
export function maskSensitiveValue(key: string, value: any): string {
    const sensitiveKeys = [
        "SECRET",
        "PASSWORD",
        "TOKEN",
        "KEY",
        "PRIVATE",
        "POSTGRES",
    ];
    const isSensitive = sensitiveKeys.some((sensitive) =>
        key.toUpperCase().includes(sensitive),
    );

    if (isSensitive && typeof value === "string" && value.length > 8) {
        return value.substring(0, 4) + "***" + value.substring(value.length - 4);
    }

    return String(value);
}

export interface BaseConfigDefinition {
    key: string;
    required: boolean;
    defaultValue?: string | boolean | number;
    /**
     * Optional override for where the value is read from.
     * - string: read from that single source key
     * - string[]: try multiple keys in order, first non-undefined wins
     */
    sourceKey?: string | string[];
}

export interface ConfigDefineOptions {
    defaultValue?: string | boolean | number;
    sourceKey?: string | string[];
}

function isConfigDefineOptions(value: unknown): value is ConfigDefineOptions {
    if (typeof value !== "object" || value === null) {
        return false;
    }

    if (Array.isArray(value)) {
        return false;
    }

    const candidate = value as Record<string, unknown>;
    return "defaultValue" in candidate || "sourceKey" in candidate;
}

export function parseValue(value: string): any {
    if (value === "true") return true;
    if (value === "false") return false;
    if (!isNaN(Number(value))) return Number(value);
    return value;
}

export abstract class BaseConfig<T extends BaseConfigDefinition> {
    protected definitions: Map<string, T> = new Map();
    protected values: Map<string, any> = new Map();
    protected initialized = false;

    // Implemented by subclasses
    protected abstract getValueFromSource(key: string): any;
    protected abstract getConfigType(): string;

    /**
     * Read directly from the underlying source (env/window/meta/etc) without requiring a prior
     * `define()` call and without applying defaults/required checks.
     *
     * This is intentionally safe to call before `initialize()` to avoid "accessed before
     * definition" ordering problems during module load.
     */
    read(key: string): any {
        return this.getValueFromSource(key);
    }

    define(
        key: string,
        required = false,
        defaultValueOrOptions?: string | boolean | number | ConfigDefineOptions,
        sourceKey?: string | string[],
    ): void {
        let defaultValue: string | boolean | number | undefined = defaultValueOrOptions as
            | string
            | boolean
            | number
            | undefined;
        let resolvedSourceKey: string | string[] | undefined = sourceKey;

        if (isConfigDefineOptions(defaultValueOrOptions)) {
            defaultValue = defaultValueOrOptions.defaultValue;
            resolvedSourceKey = defaultValueOrOptions.sourceKey ?? sourceKey;
        }

        this.definitions.set(
            key,
            { key, required, defaultValue, sourceKey: resolvedSourceKey } as T,
        );
    }

    defineMany(definitions: T[]): void {
        for (const def of definitions) {
            this.definitions.set(def.key, def);
        }
    }

    initialize(): void {
        if (this.initialized) {
            consola.warn(`${this.getConfigType()} configuration already initialized`);
            return;
        }

        // Force-load all definitions (this will validate required ones)
        for (const [key] of this.definitions) {
            // will throw if required & missing
            void this.get(key);
        }

        this.initialized = true;
    }

    set(key: string, value: any): void {
        if (!this.definitions.has(key)) {
            this.definitions.set(
                key,
                { key, required: false, defaultValue: undefined, sourceKey: undefined } as T,
            );
        }
        this.values.set(key, value);
    }

    // Single resolution path:
    // 1) explicit .set() override
    // 2) source (env/FILE/etc via getValueFromSource)
    // 3) defaultValue
    // 4) error if required
    get(key: string): any {
        const definition = this.definitions.get(key);

        // If definition doesn't exist:
        // - before initialization: allow direct source reads to avoid ordering issues
        // - after initialization: throw to catch typos and enforce "define what you use"
        if (!definition) {
            if (!this.initialized) {
                return this.read(key);
            }

            const msg = `${this.getConfigType()} configuration variable '${key}' was accessed but was never defined`;
            consola.error(`❌ ${msg}`);
            throw new Error(msg);
        }

        // 1) Explicit override
        if (this.values.has(key)) {
            return this.values.get(key);
        }

        // 2) Source (subclass decides how to read)
        const sourceLookupKeys = Array.isArray(definition.sourceKey)
            ? definition.sourceKey
            : [definition.sourceKey ?? key];

        for (const sourceLookupKey of sourceLookupKeys) {
            const sourceValue = this.getValueFromSource(sourceLookupKey);
            if (sourceValue !== undefined) {
                this.values.set(key, sourceValue);
                return sourceValue;
            }
        }

        // 3) Default
        if (definition.defaultValue !== undefined) {
            this.values.set(key, definition.defaultValue);
            return definition.defaultValue;
        }

        // 4) Required -> error
        if (definition.required) {
            const sourceKeyInfo = definition.sourceKey ? ` (source: ${definition.sourceKey})` : '';
            const msg = `Required ${this.getConfigType().toLowerCase()} configuration variable '${key}'${sourceKeyInfo} is not set and has no default value`;
            consola.error(`❌ ${msg}`);
            throw new Error(msg);
        }

        // Optional & missing
        // consola.warn(`⚠️  ${key}: not set (optional)`);
        return undefined;
    }

    getAll(): Record<string, any> {
        const result: Record<string, any> = {};
        for (const [key] of this.definitions) {
            result[key] = this.get(key);
        }
        return result;
    }

    validate(): void {
        const missing: string[] = [];

        for (const [key, definition] of this.definitions) {
            if (!definition.required) continue;

            try {
                const value = this.get(key);
                if (value === undefined || value === null || value === "") {
                    missing.push(key);
                }
            } catch {
                missing.push(key);
            }
        }

        if (missing.length > 0) {
            throw new Error(
                `Missing required ${this.getConfigType().toLowerCase()} configuration variables: ${missing.join(", ")}`,
            );
        }
    }

    isInitialized(): boolean {
        return this.initialized;
    }

    setMany(values: Record<string, any>): void {
        for (const [key, value] of Object.entries(values)) {
            if (this.definitions.has(key)) {
                this.values.set(key, value);
            }
        }
    }
}